/* Entity match verdict for the Trade Ops recaps table.
 *
 * Extracted from TradeOperationsLayout for the same reason as
 * recapGroupCompatibility: importing the layout pulls in the map/chart graph
 * (react-leaflet ships ESM jest cannot transform), so a rule left inline is a
 * rule with no pins on it.
 *
 * The rule: a bunkers deal has a SALE leg and a PURCHASE leg, and both should
 * sit on the same HMS legal entity. When they don't, HMS is selling out of one
 * company and buying into another — today nobody sees that until invoicing.
 *
 * Comparison keys on `hms_entity_id` (the resolved HMSEntity pk the backend
 * serves), never on `entity` or on the display name. The stored `entity`
 * column holds a pk-as-string on rows saved from the Bunkers/Freight dropdowns
 * and a company name on rows saved from the Solids one, so '2' and
 * 'HMS Bergbau AG' are the SAME entity — a string compare calls that a
 * mismatch and paints a healthy pair red.
 */

const MATCH = 'match';
const MISMATCH = 'mismatch';
const NEUTRAL = 'neutral';

const isFreightRow = (recap) => !!(
  recap && (recap.isFreightRecap || String(recap.recap_type || '').toUpperCase() === 'FREIGHT')
);

/* Freight and trade recaps are separate tables with their own id sequences,
 * so the merged list can hold two rows with id 4. Prefix by row kind. */
const recapRowKey = (recap) => `${isFreightRow(recap) ? 'F' : 'T'}${recap?.id}`;

/* SALE / PURCHASE / null. Freight is never a leg. Falls back to the
 * transaction-number prefix, which is what the table's own type label does
 * for rows whose recap_type never got set. */
const recapLeg = (recap) => {
  if (!recap || isFreightRow(recap)) return null;
  const declared = String(recap.recap_type || '').toUpperCase();
  if (declared === 'SALE' || declared === 'PURCHASE') return declared;
  const prefix = String(recap.transaction_number || '')[0];
  if (prefix === 'S') return 'SALE';
  if (prefix === 'P') return 'PURCHASE';
  return null;
};

/* Which deal a row belongs to.
 *
 * `entity_pair_key` (the SALE↔PURCHASE OneToOne the FuelEnquiry conversion
 * writes) comes FIRST: conversion never sets a grouping identifier, so
 * keying on grouping alone leaves the commonest Bunkers shape uncoloured.
 * Manual grouping is the fallback and is how Solids pairs its legs. */
const recapDealKey = (recap) => (
  recap?.entity_pair_key || recap?.trade_recap_grouping_identifier || null
);

/* recaps -> { [rowKey]: 'match' | 'mismatch' | 'neutral' }
 *
 * A verdict needs BOTH legs present with a resolved entity. Anything else is
 * neutral — an unfilled entity must never read as a mismatch. */
const buildRecapEntityVerdicts = (recaps) => {
  const rows = recaps || [];
  const deals = new Map();

  rows.forEach((recap) => {
    if (!recap || isFreightRow(recap)) return;
    const dealKey = recapDealKey(recap);
    const leg = recapLeg(recap);
    if (!dealKey || !leg) return;
    if (!deals.has(dealKey)) deals.set(dealKey, []);
    deals.get(dealKey).push({ recap, leg });
  });

  const verdicts = {};

  deals.forEach((legs) => {
    const resolved = legs.filter(({ recap }) => recap.hms_entity_id != null);
    const hasSale = resolved.some(({ leg }) => leg === 'SALE');
    const hasPurchase = resolved.some(({ leg }) => leg === 'PURCHASE');
    if (!hasSale || !hasPurchase) return; // stays neutral

    const ids = new Set(resolved.map(({ recap }) => recap.hms_entity_id));
    const verdict = ids.size === 1 ? MATCH : MISMATCH;
    resolved.forEach(({ recap }) => {
      verdicts[recapRowKey(recap)] = verdict;
    });
  });

  return verdicts;
};

const recapEntityVerdict = (verdicts, recap) => (
  (verdicts || {})[recapRowKey(recap)] || NEUTRAL
);

export {
  MATCH,
  MISMATCH,
  NEUTRAL,
  isFreightRow,
  recapRowKey,
  recapLeg,
  recapDealKey,
  buildRecapEntityVerdicts,
  recapEntityVerdict,
};
